<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

// Interface pour les options d'animaux
interface AnimalOption {
  value: boolean | null;
  label: string;
}

interface AnimalSearchFieldProps {
  modelValue?: boolean | null
  label?: string
  placeholder?: string
}

const props = withDefaults(defineProps<AnimalSearchFieldProps>(), {
  modelValue: null,
  label: '',
  placeholder: ''
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean | null]
}>();

const { t } = useI18n();

// Options d'animaux (même logique que RentalFiltersBar)
const animals = ref<AnimalOption[]>([]);

// Initialiser les animals au montage
const initializeAnimals = () => {
  animals.value = [
    { value: null, label: t('forms.rentalFilters.animalsPlaceholder') },
    { value: true, label: t('forms.rentalFilters.dogsAllowed') },
    { value: false, label: t('forms.rentalFilters.dogsNotAllowed') }
  ];
};

const isOpen = ref(false);
const searchTerm = ref('');
const inputRef = ref<HTMLInputElement>();

// Animal sélectionné calculé
const selectedAnimal = computed(() => {
  return animals.value.find(animal => animal.value === props.modelValue);
});

// Vérifier si une option est sélectionnée
const isSelected = (animalValue: boolean | null) => {
  // Si c'est l'option par défaut (valeur null), elle est cochée quand aucun animal n'est sélectionné
  if (animalValue === null) {
    return props.modelValue === null;
  }
  return props.modelValue === animalValue;
};

// Affichage de la valeur dans l'input
const displayValue = computed(() => {
  if (selectedAnimal.value) {
    return selectedAnimal.value.label;
  }
  return searchTerm.value || t('forms.rentalFilters.animalsPlaceholder');
});

// Filtrage des animaux basé sur la recherche
const filteredAnimals = computed(() => {
  if (!searchTerm.value) {
    return animals.value;
  }
  return animals.value.filter(animal => {
    return animal.label.toLowerCase().includes(searchTerm.value.toLowerCase());
  });
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchTerm.value = '';
    setTimeout(() => {
      inputRef.value?.focus();
    }, 0);
  }
};

const toggleAnimal = (animal: AnimalOption) => {
  // Si c'est l'option par défaut (valeur null), effacer la sélection
  if (animal.value === null) {
    emit('update:modelValue', null);
    isOpen.value = false;
    searchTerm.value = '';
    return;
  }
  
  emit('update:modelValue', animal.value);
  isOpen.value = false;
  searchTerm.value = '';
};

const clearSelection = () => {
  emit('update:modelValue', null);
  searchTerm.value = '';
};

const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchTerm.value = target.value;
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchTerm.value = '';
};

// Fermer le dropdown quand on clique à l'extérieur
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  // Éviter de fermer si on clique sur un élément du dropdown
  if (!target.closest('.search-field')) {
    closeDropdown();
  }
};

onMounted(() => {
  // Initialiser les options d'animaux
  initializeAnimals();
  
  // Écouter les clics à l'extérieur avec un délai pour éviter les conflits
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="search-field">
    <label class="field-label">
      {{ props.label || t('forms.rentalFilters.animals') }}
    </label>
    <div class="input-wrapper">
      <input
        ref="inputRef"
        type="text"
        class="search-input"
        :value="displayValue"
        :placeholder="props.placeholder || t('forms.rentalFilters.animalsPlaceholder')"
        @click="toggleDropdown"
        @input="handleInputChange" />
      <button
        type="button"
        class="dropdown-toggle"
        @click="toggleDropdown">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          :class="{ 'rotated': isOpen }">
          <path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
        </svg>
      </button>
      <button
        v-if="selectedAnimal"
        type="button"
        class="clear-button"
        @click="clearSelection">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 384 512">
          <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
        </svg>
      </button>
    </div>

    <!-- Dropdown des options -->
    <div
      v-if="isOpen"
      class="dropdown-menu">
      <div
        v-if="filteredAnimals.length === 0"
        class="no-results">
        {{ t('forms.rentalFilters.noAnimalsFound') }}
      </div>
      <label
        v-for="animal in filteredAnimals"
        :key="animal.value ? animal.value.toString() : 'default'"
        class="dropdown-item checkbox-item">
        <input
          type="checkbox"
          :checked="isSelected(animal.value)"
          @change="toggleAnimal(animal)"
          class="checkbox" />
        <span class="checkmark"></span>
        <span class="label-text">{{ animal.label }}</span>
      </label>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import './search-field-styles.scss';
</style>